import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

const RoleDropCountdown = ({ days, hours, minutes, seconds, targetDate, nextMonday }) => {
  const [showPopup, setShowPopup] = useState(false)
  const [showRoleSelectPopup, setShowRoleSelectPopup] = useState(false)
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // Check if countdown has expired
    if (days === "00" && hours === "00" && minutes === "00" && seconds === "00") {
      setIsExpired(true)
    }
  }, [days, hours, minutes, seconds])

  const togglePopup = () => setShowPopup(!showPopup)
  
  const handleFindMatches = () => {
    // Navigate directly to browse-candidates with parameter to trigger AI popup
    navigate("/browse-candidates?showAiPopup=true")
  }

  const handleRoleSelect = (roleId) => {
    setShowRoleSelectPopup(false)
    // Navigate to browse page with role ID parameter to trigger AI recommendations
    navigate(`/browse-candidates?roleId=${roleId}&startAi=true`)
  }

  // If deadline is reached, don't render anything
  if (isExpired) {
    return null
  }

  // Banner is visible only if not expired
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg relative">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
        <div className="flex-shrink-0 w-full md:w-auto">
          <div className="flex items-center justify-between md:justify-start gap-2 mb-4">
            <h2 className="text-3xl font-bold text-black">Our next role drop is in</h2>
            <button
              onClick={togglePopup}
              className="text-emerald-500 hover:text-emerald-600 bg-transparent border-none cursor-pointer"
              aria-label="What is a role drop?"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </button>
          </div>
          <div className="flex gap-3">
            <div className="flex flex-col items-center bg-emerald-500 px-3 py-2 rounded-lg w-16">
              <span className="text-2xl font-bold text-white leading-none">{days}</span>
              <span className="text-white text-xs mt-0.5">days</span>
            </div>
            <div className="flex flex-col items-center bg-emerald-500 px-3 py-2 rounded-lg w-16">
              <span className="text-2xl font-bold text-white leading-none">{hours}</span>
              <span className="text-white text-xs mt-0.5">hours</span>
            </div>
            <div className="flex flex-col items-center bg-emerald-500 px-3 py-2 rounded-lg w-16">
              <span className="text-2xl font-bold text-white leading-none">{minutes}</span>
              <span className="text-white text-xs mt-0.5">mins</span>
            </div>
            <div className="flex flex-col items-center bg-emerald-500 px-3 py-2 rounded-lg w-16">
              <span className="text-2xl font-bold text-white leading-none">{seconds}</span>
              <span className="text-white text-xs mt-0.5">secs</span>
            </div>
          </div>
        </div>
        <div className="md:max-w-xl flex-grow flex flex-col justify-between">
          {/* Updated the text styling: bold "Ready to hire?" and green for "Request to interview..." */}
          <p className="text-2xl leading-normal mb-4">
            <span className="font-bold text-black">Ready to hire?</span>{" "}
            <span className="text-emerald-500 font-semibold">
              Request to interview candidates now
            </span>{" "}
            — simply browse candidates and submit your shortlist!
          </p>
          {/* Button container with two buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/browse-candidates")}
              className="bg-emerald-500 text-white px-4 py-2 rounded hover:bg-emerald-600 transition-colors w-full sm:w-auto"
            >
              Browse Candidates
            </button>
            <button
              onClick={handleFindMatches}
              className="group inline-flex items-center justify-center px-4 py-2 border border-emerald-600 text-gray-700 bg-white rounded-md hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491] transition-all duration-150 ease-in-out w-full sm:w-auto"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth={1.5} 
                stroke="currentColor" 
                className="w-5 h-5 mr-2 text-gray-400 group-hover:text-[#02B491] transition-colors"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" 
                />
              </svg>
              <span className="text-sm font-medium">Find Matches (AI)</span>
            </button>
          </div>
        </div>
      </div>
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
            <h3 className="text-xl font-bold mb-2">What is a role drop?</h3>
            <p className="text-gray-700 mb-4">
              Our role drops are scheduled release of new opportunities for candidates. 
              We drop roles on {targetDate} at 5pm and deliver top 1% applicants to you on Tue 6th May 2025 at 9am, 
              giving candidates several days to apply. 
              It's the best way to find relevant candidates.
            </p>
            <button
              onClick={togglePopup}
              className="bg-emerald-500 text-white px-4 py-2 rounded hover:bg-emerald-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default RoleDropCountdown